import React, { useEffect, useRef, useState } from 'react';
import { KBBlock } from '../../../types';
import { renderMarkdown } from '../utils/renderMarkdown';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

const PLACEHOLDER = `# Heading 1
## Heading 2

**bold** *italic* ~~strikethrough~~ \`inline code\`

- Bullet item
- Another item

1. Ordered item
2. Second item

> Blockquote text here

\`\`\`typescript
const hello = "world";
console.log(hello);
\`\`\`

| Column 1 | Column 2 |
|----------|----------|
| Cell A   | Cell B   |

[Link text](https://example.com)`;

const MarkdownBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const text: string = block.content ?? '';
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState(false);

  // Auto-resize textarea
  useEffect(() => {
    if (isEditing && !preview && textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = 'auto';
      el.style.height = `${Math.max(180, el.scrollHeight)}px`;
    }
  }, [text, isEditing, preview]);

  // Focus on enter edit
  useEffect(() => {
    if (isEditing && !preview) {
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [isEditing, preview]);

  if (isEditing) {
    return (
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-3 py-1.5">
          <div className="flex items-center gap-1">
            {/* Quick format buttons */}
            {[
              { label: 'B', title: 'Bold', wrap: '**' },
              { label: 'I', title: 'Italic', style: 'italic', wrap: '*' },
            ].map(({ label, title, style, wrap }) => (
              <button
                key={label}
                title={title}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const el = textareaRef.current;
                  if (!el) return;
                  const { selectionStart: s, selectionEnd: e2, value } = el;
                  const selected = value.slice(s, e2) || title;
                  const wrapped = `${wrap}${selected}${wrap}`;
                  const next = value.slice(0, s) + wrapped + value.slice(e2);
                  onChange({ content: next });
                  requestAnimationFrame(() => {
                    el.selectionStart = s + wrap.length;
                    el.selectionEnd = s + wrap.length + selected.length;
                    el.focus();
                  });
                }}
                className={`w-6 h-6 text-xs font-${style ?? 'bold'} text-gray-600 hover:bg-gray-200 rounded transition-colors`}
              >
                {label}
              </button>
            ))}
            <button
              title="Inline code"
              onMouseDown={(e) => {
                e.preventDefault();
                const el = textareaRef.current;
                if (!el) return;
                const { selectionStart: s, selectionEnd: e2, value } = el;
                const selected = value.slice(s, e2) || 'code';
                const next = value.slice(0, s) + '`' + selected + '`' + value.slice(e2);
                onChange({ content: next });
              }}
              className="w-6 h-6 text-xs font-mono text-gray-600 hover:bg-gray-200 rounded transition-colors"
            >
              {'<>'}
            </button>
          </div>

          {/* Preview toggle */}
          <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-0.5">
            <button
              onClick={() => setPreview(false)}
              className={`text-xs px-2.5 py-1 rounded-md transition-colors ${!preview ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Edit
            </button>
            <button
              onClick={() => setPreview(true)}
              className={`text-xs px-2.5 py-1 rounded-md transition-colors ${preview ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Preview
            </button>
          </div>
        </div>

        {/* Edit / Preview pane */}
        {preview ? (
          <div className="min-h-[180px] p-4 bg-white">
            {text.trim()
              ? renderMarkdown(text)
              : <p className="text-gray-300 italic text-sm">Nothing to preview yet.</p>
            }
          </div>
        ) : (
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => onChange({ content: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Tab') {
                e.preventDefault();
                const el = e.currentTarget;
                const { selectionStart: s, selectionEnd: end, value } = el;
                const next = value.slice(0, s) + '  ' + value.slice(end);
                onChange({ content: next });
                requestAnimationFrame(() => {
                  el.selectionStart = el.selectionEnd = s + 2;
                });
              }
            }}
            placeholder={PLACEHOLDER}
            className="w-full min-h-[180px] px-4 py-3 font-mono text-sm text-gray-800 bg-white outline-none resize-none placeholder-gray-200 leading-relaxed"
            style={{ height: 'auto' }}
          />
        )}

        {/* Footer */}
        <div className="flex items-center justify-between bg-gray-50 border-t border-gray-200 px-3 py-1.5">
          <span className="text-xs text-gray-400">Markdown supported · Tab inserts 2 spaces</span>
          <button
            onClick={onFinish}
            className="text-xs bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // View mode
  if (!text.trim()) {
    return (
      <div className="flex items-center gap-2 py-2 px-3 text-gray-300 italic text-sm border border-dashed border-gray-200 rounded-lg">
        Empty markdown block — click to edit
      </div>
    );
  }

  return (
    <div className="prose-wrapper py-1">
      {renderMarkdown(text)}
    </div>
  );
};

export default MarkdownBlock;
